import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Modal } from "react-bootstrap";

const formatLeaveDate = (value) =>
  new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata" }).format(new Date(value));

const toLeaveInputDate = (value) => {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const dateParts = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
};

const today = toLeaveInputDate(new Date());

function LeaveManagement({ show = false, onHide }) {
  const [leave, setLeave] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const baseUrl = process.env.REACT_APP_COMPLAINTS_APP_BE_URL;

  useEffect(() => {
    if (!show) return;

    const token = localStorage.getItem("authToken");
    setLoading(true);
    axios.get(`${baseUrl}/admin-api/leave`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(({ data }) => {
        setLeave(data.leave);
        if (data.leave) {
          setFromDate(toLeaveInputDate(data.leave.fromDate));
          setToDate(toLeaveInputDate(data.leave.toDate));
          setReason(data.leave.reason || "");
        }
      })
      .catch((error) => {
        setErrorMessage(error.response?.data?.message || "Unable to load leave details.");
      })
      .finally(() => setLoading(false));
  }, [baseUrl, show]);

  const handleSetLeave = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem("authToken");
      const { data } = await axios.put(
        `${baseUrl}/admin-api/leave`,
        { fromDate, toDate, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeave(data.leave);
      setSuccessMessage("Leave has been set.");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Unable to set leave.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelLeave = async () => {
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(`${baseUrl}/admin-api/leave`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeave(null);
      setFromDate("");
      setToDate("");
      setReason("");
      setSuccessMessage("Leave has been cancelled.");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Unable to cancel leave.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Leave Management</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {leave && (
          <div className="alert alert-light border" role="status">
            <strong>Leave period:</strong>{" "}
            {formatLeaveDate(leave.fromDate)} – {formatLeaveDate(leave.toDate)}
            {leave.reason && <div className="mt-1">Reason: {leave.reason}</div>}
          </div>
        )}
        {errorMessage && <div className="alert alert-danger" role="alert">{errorMessage}</div>}
        {successMessage && <div className="alert alert-success" role="status">{successMessage}</div>}
        {loading && <p>Loading leave details...</p>}
        <form id="leave-management-form" onSubmit={handleSetLeave} className="space-y-3">
          <div>
            <label className="form-label" htmlFor="leave-from-date">From Date</label>
            <input
              id="leave-from-date"
              className="form-control"
              type="date"
              min={today}
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              required
            />
          </div>
          <div>
            <label className="form-label" htmlFor="leave-to-date">To Date</label>
            <input
              id="leave-to-date"
              className="form-control"
              type="date"
              min={fromDate || today}
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              required
            />
          </div>
          <div>
            <label className="form-label" htmlFor="leave-reason">Reason (optional)</label>
            <textarea
              id="leave-reason"
              className="form-control"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows="2"
            />
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        {leave && (
          <Button variant="outline-danger" onClick={handleCancelLeave} disabled={loading}>
            Cancel Leave
          </Button>
        )}
        <Button variant="primary" type="submit" form="leave-management-form" disabled={loading}>
          Set Leave
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default LeaveManagement;
